import React, { useState, useEffect } from "react";
import ConfirmModal from "./ConfirmModal";
import { Plus, Trash2, User, Mail, Phone, Briefcase, Loader2, AlertCircle, Check } from "lucide-react";

/**
 * ContactPersonList - Komponenta pro správu kontaktních osob zákazníka (API integrovaná)
 * 
 * Props:
 * - customerId: ID zákazníka (required)
 * - onUpdate: Callback po změně kontaktů (optional)
 */
const ContactPersonList = ({ customerId, onUpdate, onPendingContactsChange }) => {
  const [contacts, setContacts] = useState([]);
  const [pendingContacts, setPendingContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ name: "", position: "", phone: "", email: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    if (customerId) {
      fetchContacts();
    }
  }, [customerId]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/customers/${customerId}/contacts`, {
        credentials: "include"
      });
      
      if (res.ok) {
        const json = await res.json();
        setContacts(json.data || []);
      } else {
        showToast("Nepodařilo se načíst kontakty", "error");
      }
    } catch (err) {
      console.error("Error fetching contacts:", err);
      showToast("Chyba při načítání kontaktů", "error");
    }
    setLoading(false);
  };

  const validateEmail = (email) => {
    if (!email) return true; // Optional
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Jméno je povinné";
    }
    
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = "Neplatný email";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleCreate = () => {
    if (!validateForm()) return;
    
    const newContact = { ...formData, _pending: true, _tempId: Date.now() };
    const updated = [...pendingContacts, newContact];
    setPendingContacts(updated);
    if (onPendingContactsChange) onPendingContactsChange(updated);
    setIsEditing(false);
    setFormData({ name: "", position: "", phone: "", email: "" });
    setErrors({});
    showToast("Kontakt přidán – uloží se po kliknutí na Uložit", "success");
  };

  const handleUpdate = async () => {
    if (!validateForm() || !editingContact) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`/api/customers/${customerId}/contacts/${editingContact.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        await fetchContacts();
        setEditingContact(null);
        setIsEditing(false);
        setFormData({ name: "", position: "", phone: "", email: "" });
        setErrors({});
        showToast("Kontakt byl upraven", "success");
        if (onUpdate) onUpdate();
      } else {
        const json = await res.json();
        showToast(json.error || "Nepodařilo se upravit kontakt", "error");
      }
    } catch (err) {
      console.error("Error updating contact:", err);
      showToast("Chyba při úpravě kontaktu", "error");
    }
    setSubmitting(false);
  };

  const handleDelete = (contact) => {
    setConfirmAction({
      title: "Smazat kontakt?",
      message: `Opravdu chcete smazat kontakt "${contact.name}"? Tato akce je nevratná.`,
      danger: true,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          const res = await fetch(`/api/customers/${customerId}/contacts/${contact.id}`, {
            method: "DELETE",
            credentials: "include"
          });
          
          if (res.ok) {
            await fetchContacts();
            showToast("Kontakt byl smazán", "success");
            if (onUpdate) onUpdate();
          } else {
            const json = await res.json();
            showToast(json.error || "Nepodařilo se smazat kontakt", "error");
          }
        } catch (err) {
          console.error("Error deleting contact:", err);
          showToast("Chyba při mazání kontaktu", "error");
        }
      }
    });
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setIsEditing(true);
    setFormData({
      name: contact.name || "",
      position: contact.position || "",
      phone: contact.phone || "",
      email: contact.email || ""
    });
    setErrors({});
  };

  const handleCancel = () => {
    setEditingContact(null);
    setIsEditing(false);
    setFormData({ name: "", position: "", phone: "", email: "" });
    setErrors({});
  };

  const inputStyle = { 
    width: "100%", 
    padding: "10px 14px", 
    border: "1px solid #E0E0E0", 
    borderRadius: "8px", 
    fontSize: "14px", 
    boxSizing: "border-box" 
  };

  const errorInputStyle = { 
    ...inputStyle, 
    borderColor: "#E53935" 
  };

  const labelStyle = { 
    display: "block", 
    fontSize: "13px", 
    fontWeight: 500, 
    color: "#333", 
    marginBottom: "6px" 
  };

  return (
    <div style={{ marginBottom: "20px" }}>
      {/* Loading indikátor pro seznam kontaktů */}
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "16px" }}>
          <Loader2 size={20} color="#E53935" className="spin" />
        </div>
      )}

      {/* Existující kontakty */}
      {!loading && contacts.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {contacts.map((contact) => (
              <div 
                key={contact.id} 
                style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "space-between", 
                  padding: "12px", 
                  backgroundColor: editingContact?.id === contact.id ? "#FFF8E1" : "#F9F9F9", 
                  borderRadius: "8px",
                  border: editingContact?.id === contact.id ? "1px solid #FFE082" : "1px solid #EEE"
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontWeight: 600, color: "#333", margin: "0 0 4px", fontSize: "14px" }}>
                    {contact.name}
                  </p>
                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", fontSize: "13px", color: "#666" }}>
                    {contact.position && (
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Briefcase size={14} /> {contact.position}
                      </span>
                    )}
                    {contact.phone && (
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Phone size={14} /> {contact.phone}
                      </span>
                    )}
                    {contact.email && (
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Mail size={14} /> {contact.email}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                  <button 
                    type="button"
                    onClick={() => handleEdit(contact)} 
                    disabled={isEditing}
                    style={{ 
                      padding: "8px", 
                      borderRadius: "8px", 
                      border: "none", 
                      backgroundColor: "#E3F2FD", 
                      cursor: isEditing ? "not-allowed" : "pointer",
                      opacity: isEditing && editingContact?.id !== contact.id ? 0.5 : 1
                    }}
                    title="Upravit"
                  >
                    <User size={16} color="#1976D2" />
                  </button>
                  <button 
                    type="button"
                    onClick={() => handleDelete(contact)} 
                    disabled={isEditing}
                    style={{ 
                      padding: "8px", 
                      borderRadius: "8px", 
                      border: "none", 
                      backgroundColor: "#FFEBEE", 
                      cursor: isEditing ? "not-allowed" : "pointer",
                      opacity: isEditing ? 0.5 : 1
                    }}
                    title="Smazat"
                  >
                    <Trash2 size={16} color="#E53935" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending (nové) kontakty - čekají na uložení */}
      {pendingContacts.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {pendingContacts.map((contact) => (
              <div
                key={contact._tempId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px",
                  backgroundColor: "#F0F8FF",
                  borderRadius: "8px",
                  border: "1px dashed #90CAF9"
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontWeight: 600, color: "#1976D2", margin: "0 0 2px", fontSize: "14px" }}>
                    {contact.name} <span style={{ fontSize: "11px", color: "#90CAF9", fontWeight: 400 }}>• čeká na uložení</span>
                  </p>
                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", fontSize: "13px", color: "#666" }}>
                    {contact.position && <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Briefcase size={14} /> {contact.position}</span>}
                    {contact.phone && <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Phone size={14} /> {contact.phone}</span>}
                    {contact.email && <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Mail size={14} /> {contact.email}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const updated = pendingContacts.filter(c => c._tempId !== contact._tempId);
                    setPendingContacts(updated);
                    if (onPendingContactsChange) onPendingContactsChange(updated);
                  }}
                  style={{ padding: "8px", borderRadius: "8px", border: "none", backgroundColor: "#FFEBEE", cursor: "pointer" }}
                  title="Zrušit"
                >
                  <Trash2 size={16} color="#E53935" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Formulář pro úpravu existujícího kontaktu */}
      {isEditing && editingContact && (
        <div style={{ 
          padding: "16px", 
          backgroundColor: "#FFFBF0", 
          borderRadius: "8px", 
          border: "1px solid #FFE082",
          marginBottom: "16px"
        }}>
          <h4 style={{ fontSize: "14px", fontWeight: 600, color: "#333", margin: "0 0 12px" }}>
            Upravit kontaktní osobu
          </h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={labelStyle}>Jméno *</label>
              <input 
                type="text" 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={errors.name ? errorInputStyle : inputStyle}
                placeholder="Jan Novák"
              />
              {errors.name && <span style={{ color: "#E53935", fontSize: "12px" }}>{errors.name}</span>}
            </div>
            <div>
              <label style={labelStyle}>Pozice</label>
              <input 
                type="text" 
                value={formData.position} 
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                style={inputStyle}
                placeholder="Project Manager"
              />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={labelStyle}>Telefon</label>
              <input 
                type="tel" 
                value={formData.phone} 
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                style={inputStyle}
                placeholder="+420 123 456 789"
              />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input 
                type="email" 
                value={formData.email} 
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={errors.email ? errorInputStyle : inputStyle}
                placeholder="jan@email.cz"
              />
              {errors.email && <span style={{ color: "#E53935", fontSize: "12px" }}>{errors.email}</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button 
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              style={{ 
                flex: 1, 
                padding: "10px", 
                border: "1px solid #E0E0E0", 
                borderRadius: "8px", 
                backgroundColor: "#FFF", 
                cursor: submitting ? "not-allowed" : "pointer", 
                fontSize: "14px",
                opacity: submitting ? 0.6 : 1
              }}
            >
              Zrušit
            </button>
            <button 
              type="button"
              onClick={handleUpdate}
              disabled={submitting}
              style={{ 
                flex: 1, 
                padding: "10px", 
                backgroundColor: "#E53935", 
                color: "#FFF", 
                border: "none", 
                borderRadius: "8px", 
                cursor: submitting ? "not-allowed" : "pointer", 
                fontSize: "14px", 
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="spin" />
                  Ukládám...
                </>
              ) : "Uložit změny"}
            </button>
          </div>
        </div>
      )}

      {/* Tlačítko pro přidání nové kontaktní osoby */}
      {!isEditing && (
        <button
          type="button"
          onClick={() => {
            setIsEditing(true);
            setEditingContact(null);
            setFormData({ name: "", position: "", phone: "", email: "" });
            setErrors({});
          }}
          style={{
            width: "100%",
            padding: "12px",
            backgroundColor: "#E53935",
            color: "#FFF",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px"
          }}
        >
          <span style={{ fontSize: "18px", lineHeight: 1 }}>+</span> Přidat kontaktní osobu
        </button>
      )}

      {/* Formulář pro nový kontakt */}
      {isEditing && !editingContact && (
        <div style={{ 
          padding: "16px", 
          backgroundColor: "#F0F8FF", 
          borderRadius: "8px", 
          border: "1px solid #90CAF9",
          marginTop: "12px"
        }}>
          <h4 style={{ fontSize: "14px", fontWeight: 600, color: "#333", margin: "0 0 12px" }}>
            Nová kontaktní osoba
          </h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={labelStyle}>Jméno *</label>
              <input 
                type="text" 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={errors.name ? errorInputStyle : inputStyle}
                placeholder="Jan Novák"
              />
              {errors.name && <span style={{ color: "#E53935", fontSize: "12px" }}>{errors.name}</span>}
            </div>
            <div>
              <label style={labelStyle}>Pozice</label>
              <input 
                type="text" 
                value={formData.position} 
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                style={inputStyle}
                placeholder="Project Manager"
              />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={labelStyle}>Telefon</label>
              <input 
                type="tel" 
                value={formData.phone} 
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                style={inputStyle}
                placeholder="+420 123 456 789"
              />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input 
                type="email" 
                value={formData.email} 
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={errors.email ? errorInputStyle : inputStyle}
                placeholder="jan@email.cz"
              />
              {errors.email && <span style={{ color: "#E53935", fontSize: "12px" }}>{errors.email}</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button 
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              style={{ 
                flex: 1, 
                padding: "10px", 
                border: "1px solid #E0E0E0", 
                borderRadius: "8px", 
                backgroundColor: "#FFF", 
                cursor: submitting ? "not-allowed" : "pointer", 
                fontSize: "14px",
                opacity: submitting ? 0.6 : 1
              }}
            >
              Zrušit
            </button>
            <button 
              type="button"
              onClick={handleCreate}
              disabled={submitting}
              style={{ 
                flex: 1, 
                padding: "10px", 
                backgroundColor: "#E53935", 
                color: "#FFF", 
                border: "none", 
                borderRadius: "8px", 
                cursor: submitting ? "not-allowed" : "pointer", 
                fontSize: "14px", 
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="spin" />
                  Přidávám...
                </>
              ) : "Přidat"}
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!confirmAction}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmText="Smazat"
        cancelText="Zrušit"
        danger={confirmAction?.danger}
        onConfirm={confirmAction?.onConfirm}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Toast Notification */}
      {toast && (
        <div style={{ 
          position: "fixed", 
          bottom: "20px", 
          right: "20px", 
          background: toast.type === "success" ? "#4CAF50" : "#E53935", 
          color: "#FFF", 
          padding: "12px 20px", 
          borderRadius: "8px", 
          zIndex: 9999, 
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}>
          {toast.type === "success" ? <Check size={18} /> : <AlertCircle size={18} />}
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default ContactPersonList;
